'use client'

import React, { useState, useEffect, useRef } from 'react'
import { OTPInput, REGEXP_ONLY_DIGITS } from 'input-otp'
import { toast } from 'react-toastify'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faSpinner, faMobileScreen } from '@fortawesome/free-solid-svg-icons'
import { X } from 'lucide-react'
import {
  Dialog,
  DialogContent,
} from '@/components/ui/dialog'
import {
  useInitiateSmsOptInMutation,
  useVerifySmsOtpMutation,
  useResendSmsOtpMutation,
} from 'graphql/generated/graphql'
import { SMS_PHONE_VERIFICATION_ENABLED } from '@/lib/smsVerification'

// ─── Types ────────────────────────────────────────────────────────────────────

type Step = 'consent' | 'otp'

interface SmsOptInModalProps {
  open: boolean
  onClose: () => void
  onSuccess: (phoneE164: string) => void
  currentPhoneNumber?: string | null
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function formatPhoneDisplay(e164: string): string {
  const digits = e164.replace(/\D/g, '')
  if (digits.length === 11 && digits.startsWith('1')) {
    const d = digits.slice(1)
    return `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}`
  }
  return e164
}

function toE164(value: string): string {
  const digits = value.replace(/\D/g, '')
  if (digits.length === 10) return `+1${digits}`
  if (digits.length === 11 && digits.startsWith('1')) return `+${digits}`
  return value
}

function isValidE164(value: string): boolean {
  return /^\+[1-9]\d{1,14}$/.test(value) && value.length <= 16
}

// ─── Component ────────────────────────────────────────────────────────────────

const SmsOptInModal: React.FC<SmsOptInModalProps> = ({
  open,
  onClose,
  onSuccess,
  currentPhoneNumber,
}) => {
  const [step, setStep] = useState<Step>('consent')
  const [phoneInput, setPhoneInput] = useState('')
  const [phoneError, setPhoneError] = useState('')
  const [otpValue, setOtpValue] = useState('')
  const [sessionExpiry, setSessionExpiry] = useState<Date | null>(null)
  const [secondsLeft, setSecondsLeft] = useState(0)
  const [resendCooldown, setResendCooldown] = useState(0)
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null)
  const cooldownRef = useRef<ReturnType<typeof setInterval> | null>(null)

  const [initiateSmsOptIn, { loading: initiating }] = useInitiateSmsOptInMutation()
  const [verifySmsOtp, { loading: verifying }] = useVerifySmsOtpMutation()
  const [resendSmsOtp, { loading: resending }] = useResendSmsOtpMutation()

  useEffect(() => {
    if (open && currentPhoneNumber) {
      setPhoneInput(currentPhoneNumber)
    }
  }, [open, currentPhoneNumber])

  useEffect(() => {
    if (!open) {
      setStep('consent')
      setPhoneInput(currentPhoneNumber || '')
      setPhoneError('')
      setOtpValue('')
      setSessionExpiry(null)
      setSecondsLeft(0)
      clearTimers()
    }
  }, [open]) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (sessionExpiry) {
      const tick = () => {
        const remaining = Math.max(0, Math.round((sessionExpiry.getTime() - Date.now()) / 1000))
        setSecondsLeft(remaining)
        if (remaining === 0 && timerRef.current) {
          clearInterval(timerRef.current)
        }
      }
      tick()
      timerRef.current = setInterval(tick, 1000)
    }
    return () => { if (timerRef.current) clearInterval(timerRef.current) }
  }, [sessionExpiry])

  useEffect(() => {
    if (resendCooldown > 0) {
      cooldownRef.current = setInterval(() => {
        setResendCooldown((c) => {
          if (c <= 1) { clearInterval(cooldownRef.current!); return 0 }
          return c - 1
        })
      }, 1000)
    }
    return () => { if (cooldownRef.current) clearInterval(cooldownRef.current) }
  }, [resendCooldown])

  function clearTimers() {
    if (timerRef.current) clearInterval(timerRef.current)
    if (cooldownRef.current) clearInterval(cooldownRef.current)
  }

  const handleConfirmConsent = async () => {
    const e164 = toE164(phoneInput.trim())
    if (!isValidE164(e164)) {
      setPhoneError('Enter a valid US phone number (10 digits) or international number in +1XXXXXXXXXX format.')
      return
    }
    setPhoneError('')

    // Phone verification is on hold (see lib/smsVerification.ts) — capture
    // the number and skip straight to success without sending an OTP.
    if (!SMS_PHONE_VERIFICATION_ENABLED) {
      toast.success('Phone number saved.')
      onSuccess(e164)
      onClose()
      return
    }

    try {
      const res = await initiateSmsOptIn({ variables: { input: { phoneNumber: e164 } } })
      if (res.data?.initiateSmsOptIn.success) {
        const expiry = res.data.initiateSmsOptIn.sessionExpiry
          ? new Date(res.data.initiateSmsOptIn.sessionExpiry)
          : new Date(Date.now() + 10 * 60 * 1000)
        setSessionExpiry(expiry)
        setStep('otp')
        setResendCooldown(60)
      } else {
        toast.error(res.data?.initiateSmsOptIn.message || 'Could not send verification code. Please try again.')
      }
    } catch (err: any) {
      toast.error(err.message || 'Something went wrong. Please try again.')
    }
  }

  const handleVerifyOtp = async () => {
    if (otpValue.length !== 6) return
    try {
      const res = await verifySmsOtp({ variables: { input: { code: otpValue } } })
      if (res.data?.verifySmsOtp) {
        toast.success('Phone verified. SMS notifications are now enabled.')
        onSuccess(toE164(phoneInput.trim()))
        onClose()
      } else {
        toast.error('Incorrect code. Please try again.')
        setOtpValue('')
      }
    } catch (err: any) {
      const msg: string = err.message || ''
      if (msg.includes('expired')) {
        toast.error('Your code has expired. Request a new one.')
      } else if (msg.includes('locked') || msg.includes('attempts')) {
        toast.error('Too many failed attempts. Please restart the opt-in flow.')
        onClose()
      } else {
        toast.error(msg || 'Verification failed. Please try again.')
      }
      setOtpValue('')
    }
  }

  const handleResend = async () => {
    if (resendCooldown > 0 || resending) return
    try {
      const res = await resendSmsOtp()
      if (res.data?.resendSmsOtp.success) {
        const expiry = res.data.resendSmsOtp.sessionExpiry
          ? new Date(res.data.resendSmsOtp.sessionExpiry)
          : new Date(Date.now() + 10 * 60 * 1000)
        setSessionExpiry(expiry)
        setOtpValue('')
        setResendCooldown(60)
        toast.success('New code sent.')
      } else {
        const msg = res.data?.resendSmsOtp.message || ''
        if (msg.includes('limit')) {
          toast.error('Resend limit reached. Please restart the opt-in flow.')
          onClose()
        } else {
          toast.error(msg || 'Could not resend code.')
        }
      }
    } catch (err: any) {
      toast.error(err.message || 'Could not resend code.')
    }
  }

  useEffect(() => {
    if (otpValue.length === 6 && step === 'otp') {
      handleVerifyOtp()
    }
  }, [otpValue]) // eslint-disable-line react-hooks/exhaustive-deps

  const e164Preview = toE164(phoneInput.trim())
  const phoneValid = isValidE164(e164Preview)

  return (
    <Dialog open={open} onOpenChange={(isOpen) => { if (!isOpen) onClose() }}>
      <DialogContent className="max-w-md w-full p-6 rounded-2xl z-[9999]">
        {/* Icon */}
        <div className="flex justify-center">
          <div className="flex h-14 w-14 items-center justify-center rounded-full bg-[#85C349]/10">
            <FontAwesomeIcon icon={faMobileScreen} className="h-7 w-7 text-[#85C349]" />
          </div>
        </div>

        {/* ── Step 1: Consent ── */}
        {step === 'consent' && (
          <>
            <h2 className="text-center text-xl font-semibold text-gray-900">
              {SMS_PHONE_VERIFICATION_ENABLED ? 'Enable SMS notifications' : 'Add your phone number'}
            </h2>
            <p className="text-center text-sm text-gray-500">
              {SMS_PHONE_VERIFICATION_ENABLED
                ? 'Get harvest updates, engagement alerts, and account notifications by text.'
                : "We'll save this to your profile. Text notifications aren't available yet."}
            </p>

            {/* Phone number input */}
            <div>
              <label htmlFor="sms-phone" className="mb-1 block text-sm font-medium text-gray-700">
                Phone number
              </label>
              <input
                id="sms-phone"
                type="tel"
                autoFocus
                value={phoneInput}
                onChange={(e) => { setPhoneInput(e.target.value); setPhoneError('') }}
                placeholder="+1 (555) 123-4567"
                className={`h-12 w-full rounded-lg border px-4 text-base focus:outline-none focus:ring-2 focus:ring-[#85C349] ${
                  phoneError ? 'border-red-400' : 'border-gray-300'
                }`}
              />
              {phoneError && <p className="mt-1 text-xs text-red-500">{phoneError}</p>}
            </div>

            {/* TCPA disclosure — only relevant once we're actually sending texts */}
            {SMS_PHONE_VERIFICATION_ENABLED && (
              <div className="rounded-lg bg-gray-50 p-4 text-xs text-gray-600 leading-relaxed">
                <p className="font-medium text-gray-700 mb-1">Before you continue</p>
                <p>
                  By tapping <strong>Send code</strong> you agree to receive text messages from
                  Culineer, including harvest update reminders, engagement alerts, and account notifications.
                  Message frequency varies. Standard message and data rates may apply.
                </p>
                <p className="mt-2">
                  Text <strong>STOP</strong> at any time to opt out. Text <strong>HELP</strong> for support.
                </p>
              </div>
            )}

            {/* Actions on a single row */}
            <div className="flex gap-3">
              <button
                type="button"
                onClick={onClose}
                className="flex-1 rounded-full border border-gray-300 py-3 text-base font-medium text-gray-600 hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleConfirmConsent}
                disabled={initiating || !phoneInput.trim()}
                className="flex-1 rounded-full bg-[#85C349] py-3 text-base font-medium text-white transition-colors hover:bg-[#75b339] disabled:cursor-not-allowed disabled:opacity-50"
              >
                {initiating ? (
                  <FontAwesomeIcon icon={faSpinner} className="h-5 w-5 animate-spin" />
                ) : !SMS_PHONE_VERIFICATION_ENABLED ? (
                  'Save'
                ) : (
                  'Send code'
                )}
              </button>
            </div>
          </>
        )}

        {/* ── Step 2: OTP ── */}
        {step === 'otp' && (
          <>
            <h2 className="text-center text-xl font-semibold text-gray-900">
              Enter your verification code
            </h2>
            <p className="text-center text-sm text-gray-500">
              We sent a 6-digit code to{' '}
              <span className="font-medium text-gray-700">
                {phoneValid ? formatPhoneDisplay(e164Preview) : phoneInput}
              </span>
            </p>

            {/* OTP input */}
            <div className="flex justify-center">
              <OTPInput
                maxLength={6}
                pattern={REGEXP_ONLY_DIGITS}
                value={otpValue}
                onChange={setOtpValue}
                disabled={verifying}
                containerClassName="flex gap-2"
                render={({ slots }) => (
                  <>
                    {slots.map((slot, i) => (
                      <div
                        key={i}
                        className={`flex h-12 w-10 items-center justify-center rounded-lg border-2 text-xl font-semibold transition-colors ${
                          slot.isActive
                            ? 'border-[#85C349] bg-white'
                            : slot.char
                            ? 'border-gray-400 bg-white'
                            : 'border-gray-200 bg-gray-50'
                        }`}
                      >
                        {slot.char ?? (slot.isActive ? <span className="animate-pulse text-gray-300">|</span> : null)}
                      </div>
                    ))}
                  </>
                )}
              />
            </div>

            {/* Expiry countdown */}
            {secondsLeft > 0 && (
              <p className="text-center text-xs text-gray-400">
                Code expires in{' '}
                <span className={secondsLeft <= 60 ? 'text-orange-500 font-medium' : ''}>
                  {Math.floor(secondsLeft / 60)}:{String(secondsLeft % 60).padStart(2, '0')}
                </span>
              </p>
            )}
            {secondsLeft === 0 && sessionExpiry && (
              <p className="text-center text-xs text-red-500">
                Your code has expired. Request a new one below.
              </p>
            )}

            {/* Actions on a single row */}
            <div className="flex gap-3">
              <button
                type="button"
                onClick={onClose}
                className="flex-1 rounded-full border border-gray-300 py-3 text-base font-medium text-gray-600 hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleVerifyOtp}
                disabled={verifying || otpValue.length !== 6}
                className="flex-1 rounded-full bg-[#85C349] py-3 text-base font-medium text-white transition-colors hover:bg-[#75b339] disabled:cursor-not-allowed disabled:opacity-50"
              >
                {verifying ? (
                  <FontAwesomeIcon icon={faSpinner} className="h-5 w-5 animate-spin" />
                ) : (
                  'Verify'
                )}
              </button>
            </div>

            {/* Resend link */}
            <div className="text-center">
              <button
                type="button"
                onClick={handleResend}
                disabled={resendCooldown > 0 || resending}
                className="text-sm text-[#85C349] hover:underline disabled:cursor-not-allowed disabled:text-gray-400"
              >
                {resending ? (
                  <FontAwesomeIcon icon={faSpinner} className="h-4 w-4 animate-spin" />
                ) : resendCooldown > 0 ? (
                  `Resend code in ${resendCooldown}s`
                ) : (
                  'Resend code'
                )}
              </button>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  )
}

export default SmsOptInModal
